import React, { Component } from 'react';
import Alert from 'sweetalert-react';
import * as Http from '../../../http';
import Loading from '../../../components/Loading';

const COMPONENT_CAP_MR_COR = 'cap-mr-cor';
const COMPONENT_RAROC = 'raroc';
const COMPONENT_RAROC_CR = 'raroc-cr';
const CAP_PREFIX = 'STG_MR_COR_CAP_';
const CAP_PREFIX_LEGACY = 'STG_MR & COR CAP_';
const CR_PREFIX = 'RAROC_CR_';
const DATASET_PREFIX = 'RAROC_';

const day = (d) => String(d || '').slice(0, 10);
const fmtDate = (d) => (d ? String(d).replace('T', ' ').slice(0, 19) : '-');

const ensureDatasetPrefix = (raw = '') => {
    const clean = String(raw || '').trim();
    if (!clean) return '';
    if (clean.toUpperCase().startsWith(DATASET_PREFIX)) return clean;
    return `${DATASET_PREFIX}${clean}`;
};

const datasetSuffix = (fileName = '') => {
    if (String(fileName).startsWith(CAP_PREFIX))
        return String(fileName)
            .slice(CAP_PREFIX.length)
            .replace(/^RAROC_/i, '');
    if (String(fileName).startsWith(CAP_PREFIX_LEGACY))
        return String(fileName)
            .slice(CAP_PREFIX_LEGACY.length)
            .replace(/^RAROC_/i, '');
    if (String(fileName).startsWith(CR_PREFIX))
        return String(fileName)
            .slice(CR_PREFIX.length)
            .replace(/^RAROC_/i, '');
    return String(fileName || '').replace(/^RAROC_/i, '');
};

const toInputRows = (sourceItem, sourceComponent) => {
    if (!sourceItem) return [];
    let files = [];
    try {
        files = sourceItem.input_files ? JSON.parse(sourceItem.input_files) : [];
    } catch (e) {
        files = [];
    }

    return (files || []).map((f) => ({
        input: f && f.name ? f.name : '',
        // report_name phải là tên dataset gốc (vd RAROC_xxx) vì backend sẽ tự ghép
        // genNameForInputCalculationFolder(report_date, report_name) để ra thư mục.
        // Không truyền tên thư mục đã có sẵn prefix ngày (DD-MM-YYYY_RAROC_xxx) → tránh ghép 2 lần.
        downloadReportName: sourceItem.file_name,
        upload_by: sourceItem.upload_by || '-',
        upload_time: fmtDate(sourceItem.upload_time),
        report_date: day(sourceItem.report_date),
        sourceFileName: sourceItem.file_name,
        sourceComponent,
        select: false,
    }));
};

const pickLatestMeta = (capItem, crItem) => {
    const candidates = [capItem, crItem].filter(Boolean);
    if (!candidates.length) return {};
    candidates.sort((a, b) => new Date(b.upload_time || 0).getTime() - new Date(a.upload_time || 0).getTime());
    const x = candidates[0];
    return {
        status: x.status || '-',
        uploader: x.upload_by || '-',
        upload_time: fmtDate(x.upload_time),
        approver: x.approval_by || '-',
        approval_time: fmtDate(x.approval_time),
    };
};

export default class FileInfo extends Component {
    state = {
        loading: true,
        fileInfo: null,
        lstInput: [],
        isSelectAllInput: false,
        showFileDownloadError: false,
        failedDownloadsList: [],
        error: null,
    };

    componentDidMount() {
        const fileInfo = this.props.location && this.props.location.state;
        if (fileInfo) {
            this.setDatasetInfo(fileInfo);
            return;
        }
        this.loadByParams();
    }

    loadByParams = () => {
        const params = (this.props.match && this.props.match.params) || {};
        const targetDate = day(params.report_date);
        const targetDataset = ensureDatasetPrefix(decodeURIComponent(params.file_name || ''));

        Promise.all([Http.getCapMrCorList(), Http.getUploadManagement(COMPONENT_RAROC)])
            .then(([capRes, crRes]) => {
                const capRows = capRes && capRes.Success && Array.isArray(capRes.Data) ? capRes.Data : [];
                const crRowsRaw = crRes && crRes.Success && Array.isArray(crRes.Data) ? crRes.Data : [];
                const crRows = crRowsRaw.filter((row) => {
                    const name = String(row.file_name || '').toUpperCase();
                    const comp = String(row.component || '').toLowerCase();
                    return comp === COMPONENT_RAROC_CR || name.startsWith('RAROC_CR_');
                });

                const capItem = capRows.find((x) => day(x.report_date) === targetDate && ensureDatasetPrefix(datasetSuffix(x.file_name || '')) === targetDataset) || null;
                const crItem = crRows.find((x) => day(x.report_date) === targetDate && ensureDatasetPrefix(datasetSuffix(x.file_name || '')) === targetDataset) || null;

                if (!capItem && !crItem) {
                    this.setState({ loading: false, error: 'Cannot load file detail for this dataset.' });
                    return;
                }

                this.setDatasetInfo({
                    report_date: targetDate,
                    dataset: targetDataset,
                    capItem,
                    crItem,
                });
            })
            .catch(() => this.setState({ loading: false, error: 'Cannot load file detail for this dataset.' }));
    };

    setDatasetInfo = (rawInfo) => {
        const info = rawInfo || {};
        const capItem = info.capItem || null;
        const crItem = info.crItem || null;
        const dataset = ensureDatasetPrefix(info.dataset || datasetSuffix((capItem && capItem.file_name) || (crItem && crItem.file_name) || ''));
        const reportDate = day(info.report_date || (capItem && capItem.report_date) || (crItem && crItem.report_date));

        const lstInput = [...toInputRows(capItem, COMPONENT_CAP_MR_COR), ...toInputRows(crItem, COMPONENT_RAROC_CR)];
        const meta = pickLatestMeta(capItem, crItem);

        this.setState({
            loading: false,
            error: null,
            fileInfo: {
                report_date: reportDate,
                dataset,
                capItem,
                crItem,
                ...meta,
            },
            lstInput,
            isSelectAllInput: false,
        });
    };

    onSelectInput = (inputName) => {
        this.setState((prev) => {
            if (inputName === 'SELECT_ALL') {
                const nextSelectAll = !prev.isSelectAllInput;
                return {
                    isSelectAllInput: nextSelectAll,
                    lstInput: prev.lstInput.map((item) => ({ ...item, select: nextSelectAll })),
                };
            }

            const nextRows = prev.lstInput.map((item) => (item.input === inputName ? { ...item, select: !item.select } : item));
            const allChecked = nextRows.length > 0 && nextRows.every((x) => x.select);
            return { lstInput: nextRows, isSelectAllInput: allChecked };
        });
    };

    onDownloadInputFiles = () => {
        const { fileInfo, lstInput } = this.state;
        if (!fileInfo || !lstInput.length) return;

        const selected = lstInput.filter((item) => item.select).map((item) => Http.makeDownloadFileInfo(item.input, item.report_date, item.downloadReportName || item.sourceFileName, 'input_calculation', COMPONENT_RAROC));

        if (!selected.length) return;

        Http.downloadAllFiles(selected, fileInfo.report_date, fileInfo.dataset).then((result) => {
            if (result && result.is_error) {
                this.setState({
                    showFileDownloadError: true,
                    failedDownloadsList: result.failed_files || [],
                });
            }
        });
    };

    renderInputFileManagement = () => {
        return (
            <div>
                <div className='ey-file-table-toolbar'>
                    <button className='ey-dl-icon-btn' onClick={this.onDownloadInputFiles}>
                        <i className='pe-7s-download' />
                    </button>
                    <span className='ey-file-table-toolbar-label'>Download selected files</span>
                </div>
                <div
                    style={{
                        position: 'relative',
                        height: '420px',
                        overflowY: 'scroll',
                        marginBottom: '20px',
                    }}
                >
                    <table className='table ey-file-table'>
                        <thead>
                            <tr>
                                <th>
                                    <div className='form-check'>
                                        <input className='form-check-input' type='checkbox' onChange={() => this.onSelectInput('SELECT_ALL')} checked={this.state.isSelectAllInput === true} />
                                    </div>
                                </th>
                                <th>No.</th>
                                <th>Input</th>
                                <th>Uploader</th>
                                <th>Upload Time</th>
                            </tr>
                        </thead>
                        <tbody>
                            {this.state.lstInput.map((item, index) => (
                                <tr key={`${item.sourceFileName}_${item.input}_${index}`}>
                                    <td>
                                        <div className='form-check'>
                                            <input className='form-check-input' type='checkbox' onChange={() => this.onSelectInput(item.input)} checked={item.select === true} />
                                        </div>
                                    </td>
                                    <td>{index + 1}</td>
                                    <td className='ey-file-name'>{item.input}</td>
                                    <td>{item.upload_by}</td>
                                    <td>{item.upload_time}</td>
                                </tr>
                            ))}
                            {this.state.lstInput.length === 0 && (
                                <tr>
                                    <td colSpan='5' style={{ textAlign: 'center', color: '#777' }}>
                                        No uploaded input files found.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        );
    };

    renderSummary = () => {
        const info = this.state.fileInfo || {};
        return (
            <div className='header' style={{ display: 'block', textAlign: 'left', paddingLeft: 0 }}>
                <h4 className='title' style={{ margin: 0, paddingLeft: 0, textAlign: 'left' }}>
                    Report Name: {info.dataset || '-'}
                </h4>
            </div>
        );
    };

    render() {
        if (this.state.loading) {
            return (
                <div style={{ height: '65vh' }}>
                    <Loading size='small' />
                </div>
            );
        }

        if (this.state.error) {
            return (
                <div className='card'>
                    <div className='content'>
                        <div style={{ color: '#c0392b' }}>{this.state.error}</div>
                    </div>
                </div>
            );
        }

        return (
            <div>
                <div style={{ marginBottom: 10 }}>
                    <button type='button' className='btn btn-default' style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }} onClick={() => this.props.history && this.props.history.push('/raroc/file-management')}>
                        <i className='pe-7s-angle-left' />
                        Back
                    </button>
                </div>
                <div className='card'>
                    {this.renderSummary()}
                    <div className='content table-full-width'>{this.renderInputFileManagement()}</div>
                    <Alert
                        title='Some files failed to download!'
                        text={`Please check the following files: ${this.state.failedDownloadsList.join(', ')}`}
                        show={this.state.showFileDownloadError}
                        onConfirm={() => {
                            this.setState({ showFileDownloadError: false });
                        }}
                    />
                </div>
            </div>
        );
    }
}



// WEBPACK FOOTER //
// src/pages/Raroc/FileManagement/FileInfo.js